/**
 * Single source of truth for the recruitment drawer's form values.
 *
 * Open, post-save reset, candidate refresh and reopen must all derive their
 * values from here. Previously the drawer built defaults inline in three
 * places and `handleClose` called a bare `reset()`, which reset to the
 * defaults captured at first mount — before `useAuth` had resolved, so
 * `recruiterId` was `''`. Reopening the same candidate object did not change
 * the reset effect's dependencies, so the blank values survived, and because
 * `recruiterId` is required by `recruitmentFormSchema` the form then failed
 * validation silently on every submit.
 */

import type { DefaultValues } from 'react-hook-form';
import type { RecruitmentCandidate } from './types';
import type { RecruitmentFormValues } from './validations';

/**
 * Empty representation for an optional numeric field that is rendered by a
 * REGISTERED `<input>`.
 *
 * These must be `''`, not `undefined`, to match WHAT THE DOM REPORTS. An empty
 * registered input reads back as `''`, and react-hook-form syncs `_formValues`
 * from that DOM value — so a baseline of `undefined` diverges from a current
 * value of `''` and pins `isDirty` true with an empty `dirtyFields`, i.e. the
 * phantom "You have unsaved changes" prompt.
 *
 * Matching the DOM is the entire justification. Do NOT assume it also matches
 * the resolver: these fields are `z.coerce.number()…optional().or(z.literal(''))`,
 * and coercion runs FIRST — `Number('')` is `0`, which satisfies `.min(0)`, so
 * the `z.literal('')` branch is never reached. Blank therefore parses to `0`,
 * not `''` (verified). That is a pre-existing defect tracked separately: blank
 * years of experience can be persisted as 0, and a blank mentor split coerces
 * to 0 and slips past the "required when a mentor is selected" refinement.
 * This helper neither causes nor worsens it — the previous `null` baseline
 * coerced to `0` too, since `Number(null)` is also `0`.
 */
const emptyNumeric = (value: number | null | undefined) =>
  (value === null || value === undefined ? '' : value) as unknown as number;

/**
 * Build the complete form value set for a candidate.
 *
 * Returns DefaultValues<T> rather than T: several schema fields are produced by
 * z.preprocess and infer as required numbers, but the form legitimately holds
 * them empty until filled. This is the exact type useForm's `defaultValues` and
 * `reset` accept.
 *
 * @param candidate  The candidate being edited, or null/undefined when adding.
 * @param userId     Current user id, used to auto-assign the recruiter on new
 *                   records and as the fallback when a candidate has none.
 */
export function buildRecruitmentFormValues(
  candidate: RecruitmentCandidate | null | undefined,
  userId: string | undefined
): DefaultValues<RecruitmentFormValues> {
  if (!candidate) {
    return {
      firstName: '',
      lastName: '',
      preferredFirstName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      mailingStreetAddress: '',
      mailingCity: '',
      mailingState: '',
      mailingZip: '',
      nmlsNumber: '',
      dreNumber: '',
      yearsOfExperience: emptyNumeric(null),
      stateLicenses: [],
      position: undefined,
      bankedCompensation: undefined,
      brokerCommissionSplit: undefined,
      bankedOverrideBps: emptyNumeric(undefined),
      overrideRecipientId: '',
      brokerMentorCommissionSplit: emptyNumeric(undefined),
      mentorId: '',
      maximumCompensation: undefined,
      status: 'Inquiry',
      statusComment: '',
      recruiterId: userId || ''
    };
  }

  return {
    firstName: candidate.firstName,
    lastName: candidate.lastName,
    preferredFirstName: candidate.preferredFirstName || '',
    email: candidate.email,
    phone: candidate.phone,
    dateOfBirth: candidate.dateOfBirth || '',
    mailingStreetAddress: candidate.mailingStreetAddress || '',
    mailingCity: candidate.mailingCity || '',
    mailingState: candidate.mailingState || '',
    mailingZip: candidate.mailingZip || '',
    nmlsNumber: candidate.nmlsNumber,
    dreNumber: candidate.dreNumber || '',
    yearsOfExperience: emptyNumeric(candidate.yearsOfExperience),
    stateLicenses: candidate.stateLicenses,
    position: candidate.position,
    bankedCompensation: candidate.bankedCompensation,
    brokerCommissionSplit: candidate.brokerCommissionSplit,
    bankedOverrideBps: emptyNumeric(candidate.bankedOverrideBps),
    overrideRecipientId: candidate.overrideRecipientId || '',
    brokerMentorCommissionSplit: emptyNumeric(candidate.brokerMentorCommissionSplit),
    mentorId: candidate.mentorId || '',
    maximumCompensation: candidate.maximumCompensation,
    status: candidate.status,
    statusComment: candidate.statusComment || '',
    recruiterId: candidate.recruiterId || userId || ''
  };
}

/**
 * Outcome of a save attempt. The drawer needs to distinguish these three:
 * only `saved` may clear the dirty baseline or show success, and `blocked`
 * (e.g. the Talent Pipeline duplicate guard) is not an error but is also
 * not a save.
 */
export type RecruitmentSaveOutcome =
  // `candidate` is REQUIRED on success: the clean baseline must come from the
  // persisted record. Allowing it to be omitted let the drawer silently fall
  // back to the submitted values, which hides database normalisation and can
  // display a value that was never stored. Both production callers already
  // return it.
  | { status: 'saved'; candidate: RecruitmentCandidate }
  | { status: 'blocked'; message: string }
  | { status: 'failed'; message: string };
